import type { DetailedHTMLProps, ForwardRefExoticComponent, HTMLAttributes, RefAttributes, RefObject } from 'react';
import { XMarkIcon } from '@interstate/components/Icons';
import { Typography } from '@interstate/components/Typography';
import { useContextualComments } from './useContextualComments';
import { DEAL_COMMENTS_NAMESPACE } from './dealCommentsConstants';
import { PanelContentContainer } from './ContextualCommentsPanelContent.style';

type ContextualCommentsElementProps = DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> & {
    'reference-id': string;
    'common-org-id': string;
    namespace: string;
    partition: 'prod' | 'non-prod';
    link: string;
    description: string;
};

const ContextualComments = 'enterprise-product-experience-contextual-comments' as unknown as ForwardRefExoticComponent<
    ContextualCommentsElementProps & RefAttributes<HTMLElement>
>;

export type ContextualCommentsPanelContentProps = {
    referenceId: string;
    commonOrgId: string;
    launchUrl: string;
    partition: 'prod' | 'non-prod';
    isOpen: boolean;
    onClose?: () => void;
    /** Ref to the rendered web component — lets a caller's commentsUpdated listener target this
     *  exact instance instead of a global `document.querySelector`, since more than one instance
     *  of this widget can be mounted at once. */
    commentsElementRef?: RefObject<HTMLElement>;
};

export const ContextualCommentsPanelContent = ({
    referenceId,
    commonOrgId,
    launchUrl,
    partition,
    isOpen,
    onClose,
    commentsElementRef
}: ContextualCommentsPanelContentProps) => {
    const { ready: isCommentsScriptReady } = useContextualComments();

    return (
        <PanelContentContainer $isOpen={isOpen} data-testid="contextual-comments-panel-content">
            <div className="contextual-comments-panel-header">
                <Typography sx={{ marginBottom: '0', fontSize: '22px' }} variant="h2">
                    Deal Comments
                </Typography>
                {!!onClose && (
                    <XMarkIcon
                        color="gray"
                        fontSize="22px"
                        onClick={onClose}
                        style={{ cursor: 'pointer' }}
                        data-testid="deal-comments-close-icon"
                    />
                )}
            </div>
            <div className="contextual-comments-panel-body">
                {isCommentsScriptReady && (
                    <ContextualComments
                        ref={commentsElementRef}
                        reference-id={referenceId}
                        common-org-id={commonOrgId}
                        namespace={DEAL_COMMENTS_NAMESPACE}
                        partition={partition}
                        link={`${launchUrl}?dealXgId=${referenceId}&showDealMessages=true`}
                        description="Deal Comments"
                        style={{ width: '100%', height: '100%' }}
                    />
                )}
            </div>
        </PanelContentContainer>
    );
};

export default ContextualCommentsPanelContent;
